import cn from 'classnames';
import { useEffect, useState, useContext, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';

import AutoBlurButton from 'components/common/AutoBlurButton';
import Text from 'components/common/Text';
import IdMeCheckMark from 'components/icons/IdMeCheckMark';
import CaretUp from 'components/icons/navigation/CaretUp';
import IdMeVerify from 'components/shared/IdMeVerify';
import { BrandContext } from 'context/BrandContext';
import styles from 'styles/components/signUpFunnel/promo/IdMeToggle';

const IdMeToggle = ({
  hasAdditionalPromo = false,
  hasIdMe = false,
  endpoint,
  headerText,
  offerText,
  recurringDiscountText,
  isBilling = false,
}) => {
  const [showIdMeBox, setShowIdMeBox] = useState(false);
  const { brand } = useContext(BrandContext);
  const contentId = isBilling ? 'idme-billing-content' : 'idme-promo-content';
  const transitionRef = useRef();

  useEffect(() => {
    if (hasIdMe) {
      setShowIdMeBox(true);
    }
  }, [hasIdMe]);

  const ShowIdMeVerify = () => (
    <div className="px-4">
      <Text brandStylesOverrides="textSm">{offerText}</Text>
      <IdMeVerify endpoint={endpoint} brand={brand} />
    </div>
  );

  const ShowPromoText = () => (
    <>
      <IdMeCheckMark />
      <Text brandStylesOverrides="textSm text-primary" className="-mt-6 ml-10 mr-20 pb-4 bp2:mr-28">
        {hasAdditionalPromo ? recurringDiscountText : 'Your ID.me discount has been applied.'}
      </Text>
    </>
  );

  return (
    <div
      className={cn(styles.dropDownBorder, 'mt-2 rounded text-left', {
        [styles.idMeBox]: !isBilling,
        'm-auto': isBilling,
        [styles.checkoutIdMeBox]: isBilling,
      })}
    >
      <AutoBlurButton
        type="button"
        onClick={() => setShowIdMeBox(!showIdMeBox)}
        className="btn flex h-20 w-full items-center py-0 pl-0 text-left leading-6"
        aria-expanded={showIdMeBox}
        aria-controls={contentId}
        id={`${contentId}-button`}
      >
        <Text
          htmlElement="span"
          brandStylesOverrides="textMd"
          className="mb-0 w-full px-4 py-4 pr-6 text-charcoal-100"
        >
          {headerText}
        </Text>
        <CaretUp
          className={cn('h-10 w-10 text-charcoal-100 transition-all bp2:h-8 bp2:w-8', {
            ['rotate-180 transform']: !showIdMeBox,
          })}
        />
      </AutoBlurButton>
      <CSSTransition
        nodeRef={transitionRef}
        in={showIdMeBox}
        timeout={500}
        classNames={{
          enter: 'fadeInOut-enter scaleHeight-enter',
          enterActive: 'fadeInOut-enter-active scaleHeight-enter-active',
          exit: 'fadeInOut-exit scaleHeight-exit',
          exitActive: 'fadeInOut-exit-active scaleHeight-exit-active',
        }}
        unmountOnExit
      >
        <div
          ref={transitionRef}
          id={contentId}
          role="region"
          aria-labelledby={`${contentId}-button`}
        >
          {hasIdMe ? <ShowPromoText /> : <ShowIdMeVerify />}
        </div>
      </CSSTransition>
    </div>
  );
};

IdMeToggle.propTypes = {
  endpoint: PropTypes.string.isRequired,
  hasAdditionalPromo: PropTypes.bool,
  hasIdMe: PropTypes.bool,
  headerText: PropTypes.string.isRequired,
  recurringDiscountText: PropTypes.string,
  offerText: PropTypes.string.isRequired,
  isBilling: PropTypes.bool,
};

export default IdMeToggle;
